import { DateTime } from 'luxon';
import { EnrichedOrder } from './view';

/*
 * Utility function to filter orders into three distinct groups:
 * 1. Orders that are a day or more away.
 * 2. Orders due later today (> 30 minutes, same day).
 * 3. All other orders (older pickupTime, <= 30 min, or no pickupTime).
 */
export const filterOrders = (
  enrichedOrders: EnrichedOrder[],
  getEndDate: (date: Date) => Date,
) => {
  const now = DateTime.now();
  const endOfDay = DateTime.fromJSDate(getEndDate(new Date())); // End of the current day in UTC

  const firedOrders = enrichedOrders.filter((enrichedOrder) => enrichedOrder.order.fired !== false);
  const upcomingOrders = enrichedOrders.filter((enrichedOrder) => enrichedOrder.order.fired === false);

  const ordersDayOrMoreAway = firedOrders.filter((enrichedOrder) => {
    const pickupTimeRaw =
      enrichedOrder.realOrder?.desiredTime || enrichedOrder.order.estimatedFinishTime;

    if (!pickupTimeRaw) return false;
    const pickupDateTime = DateTime.fromISO(pickupTimeRaw);

    // Include orders where pickupTime is later than the end of the current day
    return pickupDateTime > endOfDay;
  });

  const ordersDueLaterToday = firedOrders.filter((enrichedOrder) => {
    const pickupTimeRaw =
      enrichedOrder.realOrder?.desiredTime || enrichedOrder.order.estimatedFinishTime;

    if (!pickupTimeRaw) return false;
    const pickupDateTime = DateTime.fromISO(pickupTimeRaw);
    const pickupMinutes = pickupDateTime.diff(now, 'minutes').minutes;

    // Same day, > 30 min, and not beyond end of day
    return pickupDateTime <= endOfDay && pickupMinutes > 30;
  });

  const regularKDSOrders = firedOrders.filter((enrichedOrder) => {
    const pickupTimeRaw =
      enrichedOrder.realOrder?.desiredTime || enrichedOrder.order.estimatedFinishTime;

    if (!pickupTimeRaw) return true; // Include orders with no pickupTime
    const pickupDateTime = DateTime.fromISO(pickupTimeRaw);
    const pickupMinutes = pickupDateTime.diff(now, 'minutes').minutes;

    // Include orders due <= 30 min, past, or without pickupTime
    return isNaN(pickupMinutes) || pickupMinutes <= 30 || pickupDateTime < now;
  });

  console.log('[KDS filterOrders]', {
    total: enrichedOrders.length,
    fired: firedOrders.length,
    upcoming: upcomingOrders.length,
    upcomingOrderIds: upcomingOrders.map((o) => ({ id: o.order.id, courseId: o.order.courseId, fired: o.order.fired })),
  });

  return { ordersDayOrMoreAway, ordersDueLaterToday, regularKDSOrders, upcomingOrders };
};

/*
 * Groups orders by their pickup date and returns them in chronological order
 * Returns an array of objects containing the date and orders for that date
 */
export const groupOrdersByDate = (orders: EnrichedOrder[]) => {
  // Group orders by date
  const ordersByDate: { [key: string]: EnrichedOrder[] } = {};

  orders.forEach((enrichedOrder) => {
    const pickupTimeRaw =
      enrichedOrder.realOrder?.desiredTime || enrichedOrder.order.estimatedFinishTime;

    if (!pickupTimeRaw) return;

    const pickupDateTime = DateTime.fromISO(pickupTimeRaw);
    const dateKey = pickupDateTime.toISODate(); // YYYY-MM-DD format

    if (!dateKey) return;

    if (!ordersByDate[dateKey]) {
      ordersByDate[dateKey] = [];
    }

    ordersByDate[dateKey].push(enrichedOrder);
  });

  // Sort by date and convert to array format
  const sortedDates = Object.keys(ordersByDate).sort();

  return sortedDates.map((date) => ({
    date,
    orders: ordersByDate[date],
  }));
};
